import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Button, Modal, Form, Input, InputNumber, Select,
  Space, Tag, Typography, message, Popconfirm, Switch, Empty, Spin,
  Divider, Tooltip,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  FileTextOutlined, LockOutlined, UnlockOutlined, AppstoreOutlined,
  SafetyCertificateOutlined, TrophyOutlined, ClockCircleOutlined,
  CheckCircleOutlined, OrderedListOutlined, CloseOutlined,
} from '@ant-design/icons';
import { govtApi } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ACCENT_COLORS = [
  { label: 'Royal Blue',    value: '#1D4ED8' },
  { label: 'Emerald',       value: '#059669' },
  { label: 'Purple',        value: '#7C3AED' },
  { label: 'Amber',         value: '#D97706' },
  { label: 'Rose',          value: '#E11D48' },
  { label: 'Cyan',          value: '#0891B2' },
  { label: 'Indigo',        value: '#4338CA' },
  { label: 'Teal',          value: '#0F766E' },
];

const TestSeriesPage: React.FC = () => {
  const [series, setSeries]     = useState<any[]>([]);
  const [exams, setExams]       = useState<any[]>([]);
  const [tests, setTests]       = useState<any[]>([]);
  const [loading, setLoading]   = useState(false);
  const [filterExam, setFilterExam] = useState<string>('');

  // Series modal
  const [seriesModal, setSeriesModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState<any>(null);
  const [seriesForm] = Form.useForm();

  // Tests-in-series modal
  const [testsModal, setTestsModal]         = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<any>(null);
  const [seriesTests, setSeriesTests]       = useState<any[]>([]);
  const [addTestId, setAddTestId]           = useState('');
  const [addFreePreview, setAddFreePreview] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sr, ex] = await Promise.all([
        govtApi.get('/govt/admin/series'),
        govtApi.get('/govt/admin/exams'),
      ]);
      setSeries(sr.data?.data ?? sr.data ?? []);
      const examList = Array.isArray(ex.data) ? ex.data : (ex.data?.data ?? []);
      setExams(examList);
    } catch { message.error('Failed to load test series'); }
    finally { setLoading(false); }
  };

  const loadSeriesTests = async (seriesId: string) => {
    try {
      const r = await govtApi.get(`/govt/admin/series/${seriesId}/tests`);
      setSeriesTests(r.data?.data ?? r.data ?? []);
    } catch { message.error('Failed to load series tests'); }
  };

  const loadAvailableTests = async (examId?: string) => {
    try {
      const r = await govtApi.get(`/govt/admin/tests${examId ? `?examId=${examId}` : ''}`);
      setTests(Array.isArray(r.data) ? r.data : (r.data?.data ?? []));
    } catch {}
  };

  const openSeriesModal = (s?: any) => {
    setEditingSeries(s ?? null);
    seriesForm.setFieldsValue(s ?? { is_free: true, is_active: true, accent_color: '#1D4ED8', price: 0 });
    setSeriesModal(true);
  };

  const saveSeries = async () => {
    const v = await seriesForm.validateFields();
    try {
      if (editingSeries) {
        await govtApi.put(`/govt/admin/series/${editingSeries.id}`, v);
        message.success('Series updated');
      } else {
        await govtApi.post('/govt/admin/series', v);
        message.success('Series created');
      }
      setSeriesModal(false);
      loadAll();
    } catch (e: any) { message.error(e.response?.data?.error || 'Save failed'); }
  };

  const deleteSeries = async (id: string) => {
    try {
      await govtApi.delete(`/govt/admin/series/${id}`);
      message.success('Deleted');
      loadAll();
    } catch { message.error('Delete failed'); }
  };

  const openTestsModal = async (s: any) => {
    setSelectedSeries(s);
    await loadSeriesTests(s.id);
    await loadAvailableTests(s.exam_id);
    setTestsModal(true);
  };

  const addTest = async () => {
    if (!addTestId) { message.warning('Select a test first'); return; }
    try {
      await govtApi.post(`/govt/admin/series/${selectedSeries.id}/tests`, {
        test_id: addTestId,
        sort_order: seriesTests.length,
        is_free_preview: addFreePreview,
      });
      message.success('Test added to series');
      setAddTestId('');
      await loadSeriesTests(selectedSeries.id);
      loadAll();
    } catch (e: any) { message.error(e.response?.data?.error || 'Failed'); }
  };

  const removeTest = async (testId: string) => {
    try {
      await govtApi.delete(`/govt/admin/series/${selectedSeries.id}/tests/${testId}`);
      message.success('Removed');
      await loadSeriesTests(selectedSeries.id);
      loadAll();
    } catch { message.error('Failed'); }
  };

  const filtered = filterExam ? series.filter(s => s.exam_id === filterExam) : series;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#0F172A' }}>
            <AppstoreOutlined style={{ color: '#7C3AED', marginRight: 10 }} />
            Test Series
          </Title>
          <Text type="secondary">Curated multi-test packages for govt exam aspirants</Text>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />} size="large"
          style={{ borderRadius: 10, height: 44, background: '#7C3AED', borderColor: '#7C3AED' }}
          onClick={() => openSeriesModal()}>
          New Series
        </Button>
      </div>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Series', value: series.length,                                       color: '#7C3AED', bg: '#F5F3FF', icon: <AppstoreOutlined /> },
          { label: 'Free Series',  value: series.filter(s => s.is_free).length,                color: '#059669', bg: '#ECFDF5', icon: <UnlockOutlined /> },
          { label: 'Paid Series',  value: series.filter(s => !s.is_free).length,               color: '#D97706', bg: '#FFFBEB', icon: <LockOutlined /> },
          { label: 'Active',       value: series.filter(s => s.is_active).length,              color: '#1D4ED8', bg: '#EFF6FF', icon: <CheckCircleOutlined /> },
        ].map(s => (
          <Col xs={12} lg={6} key={s.label}>
            <Card style={{ borderRadius: 14, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
              bodyStyle={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: s.bg, color: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                  {s.icon}
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{s.label}</Text>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter bar */}
      <Card style={{ borderRadius: 14, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: 20 }}
        bodyStyle={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text strong style={{ color: '#6B7280', fontSize: 13 }}>Filter by Exam:</Text>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[{ id: '', name: 'All Exams', code: '' }, ...exams].map(e => {
              const active = filterExam === e.id;
              return (
                <button key={e.id} onClick={() => setFilterExam(e.id)} style={{
                  padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
                  border: active ? 'none' : '1.5px solid #E5E7EB',
                  background: active ? '#7C3AED' : '#fff',
                  color: active ? '#fff' : '#374151', fontWeight: active ? 700 : 400,
                  transition: 'all 0.2s',
                }}>
                  {e.name}{e.code ? ` (${e.code})` : ''}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Series grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : filtered.length === 0 ? (
        <Empty image={<AppstoreOutlined style={{ fontSize: 56, color: '#D1D5DB' }} />}
          description={<Text type="secondary">No series yet — create your first test series</Text>}
          style={{ padding: '60px 0' }} />
      ) : (
        <Row gutter={[20, 20]}>
          {filtered.map(s => (
            <Col xs={24} sm={12} xl={8} key={s.id}>
              <Card
                hoverable
                style={{ borderRadius: 18, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', overflow: 'hidden' }}
                bodyStyle={{ padding: 0 }}>

                {/* Gradient header */}
                <div style={{
                  background: `linear-gradient(135deg, ${s.accent_color} 0%, ${s.accent_color}CC 100%)`,
                  padding: '24px 20px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, color: '#fff',
                    }}>
                      <AppstoreOutlined />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Tag style={{
                        borderRadius: 20, border: 'none', fontWeight: 700, fontSize: 10,
                        background: s.is_free ? 'rgba(16,185,129,0.25)' : 'rgba(251,191,36,0.25)',
                        color: s.is_free ? '#6EE7B7' : '#FCD34D', padding: '3px 10px',
                      }}>
                        {s.is_free ? 'FREE' : `₹${Number(s.price).toFixed(0)}`}
                      </Tag>
                      <Tag style={{
                        borderRadius: 20, border: 'none', fontSize: 10, padding: '3px 10px',
                        background: s.is_active ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.1)',
                        color: s.is_active ? '#6EE7B7' : 'rgba(255,255,255,0.5)',
                      }}>
                        {s.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </Tag>
                    </div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <Text strong style={{ color: '#fff', fontSize: 17, display: 'block', lineHeight: '22px' }}>
                      {s.title}
                    </Text>
                    {s.exam_name && (
                      <Tag style={{
                        marginTop: 8, borderRadius: 20, border: 'none', fontSize: 11, fontWeight: 600,
                        background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)',
                      }}>
                        <SafetyCertificateOutlined style={{ marginRight: 4 }} />{s.exam_name}
                      </Tag>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '16px 20px' }}>
                  {s.description && (
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14, lineHeight: '18px' }}>
                      {s.description}
                    </Text>
                  )}

                  <Row gutter={0} style={{ marginBottom: 16 }}>
                    {[
                      { icon: <FileTextOutlined />, label: 'Tests', value: s.test_count ?? s.total_tests ?? 0 },
                      { icon: <TrophyOutlined />,   label: 'Enrolled', value: s.enrolled_count ?? 0 },
                    ].map(m => (
                      <Col span={12} key={m.label}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#9CA3AF', fontSize: 14 }}>{m.icon}</span>
                          <div>
                            <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>{m.label}</Text>
                            <Text strong style={{ fontSize: 16 }}>{m.value}</Text>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  <Divider style={{ margin: '0 0 14px' }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      icon={<OrderedListOutlined />}
                      style={{ flex: 1, borderRadius: 8, height: 34, borderColor: s.accent_color, color: s.accent_color }}
                      onClick={() => openTestsModal(s)}>
                      Manage Tests
                    </Button>
                    <Tooltip title="Edit">
                      <Button icon={<EditOutlined />} style={{ borderRadius: 8, height: 34 }}
                        onClick={() => openSeriesModal(s)} />
                    </Tooltip>
                    <Popconfirm title="Delete this series?" onConfirm={() => deleteSeries(s.id)} okType="danger">
                      <Button danger icon={<DeleteOutlined />} style={{ borderRadius: 8, height: 34 }} />
                    </Popconfirm>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Create / Edit Modal ── */}
      <Modal
        title={<span style={{ fontWeight: 700 }}>{editingSeries ? 'Edit Test Series' : 'Create Test Series'}</span>}
        open={seriesModal} onOk={saveSeries} onCancel={() => setSeriesModal(false)}
        okText={editingSeries ? 'Update' : 'Create'} width={540}
        styles={{ body: { paddingTop: 16 } }}>
        <Form form={seriesForm} layout="vertical">
          <Form.Item name="title" label="Series Title" rules={[{ required: true }]}>
            <Input placeholder="e.g. KSP PC Complete Series 2026" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Form.Item name="exam_id" label="Exam" rules={[{ required: true }]}>
            <Select
              placeholder="Select exam"
              options={exams.map(e => ({ value: e.id, label: `${e.name} (${e.code})` }))}
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="What's included in this series?" style={{ borderRadius: 8 }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="accent_color" label="Theme Color">
                <Select>
                  {ACCENT_COLORS.map(c => (
                    <Select.Option key={c.value} value={c.value}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 16, height: 16, borderRadius: 4, background: c.value }} />
                        {c.label}
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_free" label="Free Access" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="Free" unCheckedChildren="Paid" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="price" label="Price (₹)" initialValue={0}>
                <InputNumber min={0} step={10} style={{ width: '100%', borderRadius: 8 }} prefix="₹" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Manage Tests Modal ── */}
      <Modal
        title={
          <div>
            <Text strong style={{ fontSize: 16 }}>{selectedSeries?.title}</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Manage tests in this series</Text></div>
          </div>
        }
        open={testsModal} footer={null} onCancel={() => setTestsModal(false)}
        width={680} styles={{ body: { paddingTop: 16 } }}>

        {/* Add test row */}
        <Card style={{ borderRadius: 12, background: '#F9FAFB', border: '1px solid #F3F4F6', marginBottom: 20 }}
          bodyStyle={{ padding: '14px 16px' }}>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>Add a Test</Text>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Select
              placeholder="Select test to add"
              style={{ flex: 1, minWidth: 200 }}
              value={addTestId || undefined}
              onChange={setAddTestId}
              options={tests
                .filter(t => !seriesTests.find(st => st.test_id === t.id))
                .map(t => ({ value: t.id, label: t.title }))}
              showSearch
              filterOption={(input, opt) => (opt?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Switch size="small" checked={addFreePreview} onChange={setAddFreePreview} />
              <Text style={{ fontSize: 12 }}>Free preview</Text>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={addTest}
              style={{ borderRadius: 8, background: selectedSeries?.accent_color }}>
              Add
            </Button>
          </div>
        </Card>

        {/* Tests list */}
        {seriesTests.length === 0 ? (
          <Empty description="No tests added yet" style={{ padding: '32px 0' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {seriesTests.map((item, i) => (
              <div key={item.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', borderRadius: 12,
                border: '1px solid #F3F4F6', background: '#fff',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: '#EFF6FF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#1D4ED8', fontWeight: 700, fontSize: 13, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 14, display: 'block' }}>{item.title}</Text>
                  <Space size={6} style={{ marginTop: 4 }}>
                    {item.total_questions && <Tag style={{ borderRadius: 6, fontSize: 11 }}>{item.total_questions} Q</Tag>}
                    {item.duration_minutes && <Tag style={{ borderRadius: 6, fontSize: 11 }}><ClockCircleOutlined /> {item.duration_minutes}m</Tag>}
                    {item.is_free_preview && <Tag color="green" style={{ borderRadius: 6, fontSize: 11 }}>Free Preview</Tag>}
                  </Space>
                </div>
                <Popconfirm title="Remove from series?" onConfirm={() => removeTest(item.test_id)} okType="danger">
                  <Button type="text" danger icon={<CloseOutlined />} size="small" />
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TestSeriesPage;
